import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../services/supabase';
import type { Periodo, PeriodoCreatePayload, PeriodoUpdatePayload } from '../types/periodo';

const QK = {
  all: ['periodos'] as const,
  list: (incluirInactivos: boolean) =>
    ['periodos', 'list', { incluirInactivos }] as const,
};

// ----- LIST -----

export function usePeriodos(incluirInactivos = true) {
  return useQuery({
    queryKey: QK.list(incluirInactivos),
    queryFn: async () => {
      let query = supabase
        .from('periodos')
        .select('*')
        .order('periodo', { ascending: false })
        .order('excepcional', { ascending: true });
      if (!incluirInactivos) query = query.eq('estado', 'activo');
      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as Periodo[];
    },
  });
}

// ----- CREATE -----

async function createPeriodo(payload: PeriodoCreatePayload): Promise<Periodo> {
  const { data, error } = await supabase
    .from('periodos')
    .insert(payload)
    .select('*')
    .single();
  if (error) throw error;
  return data as Periodo;
}

export function useCreatePeriodo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createPeriodo,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.all });
    },
  });
}

// ----- UPDATE -----

async function updatePeriodo({
  id,
  payload,
}: {
  id: string;
  payload: PeriodoUpdatePayload;
}): Promise<Periodo> {
  const { data, error } = await supabase
    .from('periodos')
    .update(payload)
    .eq('id', id)
    .select('*')
    .single();
  if (error) throw error;
  return data as Periodo;
}

export function useUpdatePeriodo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: updatePeriodo,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.all });
    },
  });
}

// ----- TOGGLE ACTIVO -----

export function useTogglePeriodo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, activo }: { id: string; activo: boolean }) => {
      const { data, error } = await supabase
        .from('periodos')
        .update({ estado: activo ? 'activo' : 'inactivo' })
        .eq('id', id)
        .select('*')
        .single();
      if (error) throw error;
      return data as Periodo;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.all });
    },
  });
}
